import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faBuilding,
  faFileAlt,
  faChartBar,
  faExclamationTriangle,
  faShieldAlt,
  faDatabase,
  faLock,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faBuilding as faBuildingDefault,
  faDownload,
  faEye,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../../utils/constant';
import "./dashboardVendor.css";
import Pagination from "../Pagination/Pagination";

const DashboardVendor = () => {
  const [vendorFindings, setVendorFindings] = useState(null);
  const [assessmentStats, setAssessmentStats] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [riskOverview, setRiskOverview] = useState(null);
  const [companyLists, setCompanyLists] = useState({});
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState({
    findings: false,
    assessments: false,
    reviews: false,
    riskOverview: false,
    companies: false,
    report: false
  });
  const [cveDetails, setCveDetails] = useState(null);
  const [loadingCveDetails, setLoadingCveDetails] = useState(false);
  const [showCveModal, setShowCveModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [highRiskDetails, setHighRiskDetails] = useState(null);
  const [sslCertDetails, setSslCertDetails] = useState(null);
  const [vulnSslCertDetails, setVulnSslCertDetails] = useState(null);
  const [obsoleteTechDetails, setObsoleteTechDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentDetailsType, setCurrentDetailsType] = useState(null);
  const [stolenCredDetails, setStolenCredDetails] = useState(null);
  const [malwareCredDetails, setMalwareCredDetails] = useState(null);
  const [leakedSessionDetails, setLeakedSessionDetails] = useState(null);
  const [leakedCodeDetails, setLeakedCodeDetails] = useState(null);
  const [danglingSubdomainDetails, setDanglingSubdomainDetails] = useState(null);
  const [darkWebMentionDetails, setDarkWebMentionDetails] = useState(null);
  const [piiDetails, setPiiDetails] = useState(null);
  const [postmanDetails, setPostmanDetails] = useState(null);
  const [telegramDetails, setTelegramDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [spfDetails, setSpfDetails] = useState(null);
  const [dmarcDetails, setDmarcDetails] = useState(null);

  const org = JSON.parse(localStorage.getItem("org") || "{}");

  const { orgId } = useParams();
  const id = useSelector(state => {
    if (orgId) {
      return orgId;
    } else {
      return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
    }
  });

  useEffect(() => {
    const fetchVendorFindings = async () => {
      if (!id) return;

      try {
        setLoading(prev => ({ ...prev, findings: true }));
        const session = localStorage.getItem("session");
        const response = await axios.get(
          `${API_URL}/forest-view/organisations/${id}/risk-distribution`,
          {
            headers: {
              Authorization: `Bearer ${session}`
            }
          }
        );
        setVendorFindings(response.data);
        setLoading(prev => ({ ...prev, findings: false }));
      } catch (error) {
        setLoading(prev => ({ ...prev, findings: false }));
      }
    };

    const fetchRiskOverview = async () => {
      if (!id) return;

      try {
        setLoading(prev => ({ ...prev, riskOverview: true }));
        const session = localStorage.getItem("session");
        const response = await axios.get(
          `${API_URL}/tprm/risk-totals/organisationId/${id}`,
          {
            headers: {
              Authorization: `Bearer ${session}`
            }
          }
        );
        setRiskOverview(response.data);
        setLoading(prev => ({ ...prev, riskOverview: false }));
      } catch (error) {
        // console.error removed
        setLoading(prev => ({ ...prev, riskOverview: false }));
      }
    };

    const fetchAssessmentStats = async () => {
      if (!id) return;

      try {
        setLoading(prev => ({ ...prev, assessments: true }));
        const session = localStorage.getItem("session");
        const response = await axios.get(
          `${API_URL}/tprm/${id}/questioner/sent/stats`,
          {
            headers: {
              Authorization: `Bearer ${session}`
            }
          }
        );
        setAssessmentStats(response.data.data);
        setLoading(prev => ({ ...prev, assessments: false }));
      } catch (error) {
        // console.error removed
        setLoading(prev => ({ ...prev, assessments: false }));
      }
    };

    const fetchPendingReviews = async () => {
      if (!id) return;

      try {
        setLoading(prev => ({ ...prev, reviews: true }));
        const session = localStorage.getItem("session");
        const response = await axios.get(
          `${API_URL}/tprm/${id}/questioner/sent?page=1&limit=5&status=submitted`,
          {
            headers: {
              Authorization: `Bearer ${session}`
            }
          }
        );
        setPendingReviews(response.data.data);
        setLoading(prev => ({ ...prev, reviews: false }));
      } catch (error) {
        // console.error removed
        setLoading(prev => ({ ...prev, reviews: false }));
      }
    };

    fetchVendorFindings();
    fetchRiskOverview();
    fetchAssessmentStats();
    fetchPendingReviews();
  }, [id]); // Add id as a dependency

  // Helper function to extract number from risk string
  const extractNumber = (riskString) => {
    const match = riskString.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Helper function to get icon based on module
  const getModuleIcon = (module) => {
    switch (module) {
      case 'high-risk-vulnerability':
      case 'expired-ssl-certificate':
      case 'vulnerable-ssl-certificate':
      case 'vulnerable-obsolete-technology':
        return faShieldAlt;
      case 'stolen-cred':
      case 'malware-cred':
      case 'personal-information-breach':
        return faDatabase;
      default:
        return faLock;
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'submitted':
        return 'status-badge submitted';
      case 'in_progress':
        return 'status-badge in-progress';
      case 'overdue':
        return 'status-badge overdue';
      default:
        return 'status-badge pending';
    }
  };

  // Add new function to fetch company list
  const fetchCompanyList = async (module_name) => {
    if (!id) return;

    try {
      setLoading(prev => ({ ...prev, companies: true }));
      const session = localStorage.getItem("session");
      const response = await axios.get(
        `${API_URL}/forestView/organizations/${id}/stats/${module_name}/list/companies`,
        {
          headers: {
            Authorization: `Bearer ${session}`
          }
        }
      );
      setCompanyLists(prev => ({
        ...prev,
        [module_name]: response.data.data
      }));
      setLoading(prev => ({ ...prev, companies: false }));
    } catch (error) {
      // console.error removed
      setLoading(prev => ({ ...prev, companies: false }));
    }
  };

  const handleViewCompanies = (finding) => {
    setSelectedFinding(finding);
    if (!companyLists[finding.module]) {
      fetchCompanyList(finding.module);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedFinding(null);
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, report: true }));
      const session = localStorage.getItem("session");
      const response = await axios.get(
        `${API_URL}/tprm/organisation/${id}/consolidated-report`,
        {
          headers: {
            Authorization: `Bearer ${session}`
          },
          responseType: 'blob' // Important for handling PDF download
        }
      );

      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `consolidated-report-${new Date().toISOString().split('T')[0]}.pdf`;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      window.URL.revokeObjectURL(url);

      setLoading(prev => ({ ...prev, report: false }));
    } catch (error) {
      // console.error removed
      setLoading(prev => ({ ...prev, report: false }));
      // You might want to show an error message to the user here
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1) {
      handleViewDetails(selectedOrgId, currentDetailsType, newPage, searchTerm);
    }
  };

  const closeCveModal = () => {
    setShowCveModal(false);
    setCveDetails(null);
    setSelectedOrgId(null);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    handleViewDetails(selectedOrgId, currentDetailsType, 1, e?.target?.value);
  };

  const handleViewDetails = async (orgId, findingType, page = 1, search = '') => {
    try {
      setLoadingDetails(true);
      setSelectedOrgId(orgId);
      setCurrentDetailsType(findingType);
      const session = localStorage.getItem("session");

      let response;
      switch (findingType) {
        case 'high-risk-vulnerability':
          response = await axios.get(
            `${API_URL}/cve-details/${orgId}/delta?page=${page}&limit=${itemsPerPage}&severity=&category=all&ip=&minSeverity=high&search=${search}`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setHighRiskDetails(response.data);
          break;
        case 'expired-ssl-certificate':
          response = await axios.get(
            `${API_URL}/expired-ssl-certificates/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&domain_search_query=${search}&status=expired&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setSslCertDetails(response.data);
          break;
        case 'vulnerable-ssl-certificate':
          response = await axios.get(
            `${API_URL}/expired-ssl-certificates/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&domain_search_query=${search}&status=hasWeakCiphers&from=&till=&status=vulnerable`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setVulnSslCertDetails(response.data);
          break;
        case 'vulnerable-obsolete-technology':
          response = await axios.get(
            `${API_URL}/webtech_analysis/${orgId}/delta?pageno=${page}&limit=${itemsPerPage}&category=all&searchBy=subDomainName&name=${search}&type=Vulnerable&minExploitabilityScore=&severity=&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setObsoleteTechDetails(response?.data);
          break;
        case 'stolen-cred':
          response = await axios.get(
            `${API_URL}/dehashed/search/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&type=all&emailquery=${search}&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setStolenCredDetails(response.data);
          break;
        case 'malware-cred':
          response = await axios.get(
            `${API_URL}/getMalwareCredentials/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&userquery=${search}&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setMalwareCredDetails(response.data);
          break;
        case 'personal-information-breach':
          response = await axios.get(
            `${API_URL}/pii-details/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&email_search_query=${search}&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setPiiDetails(response.data);
          break;
        case 'leaked-session':
          response = await axios.get(
            `${API_URL}/leaked-sessions/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&searchBy=subDomainName=${search}&type=all&minExploitabilityScore=&severity=&from=&till=&search=${search}`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setLeakedSessionDetails(response.data);
          break;
        case 'leaked-code':
          response = await axios.get(
            `${API_URL}/approved-code/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&searchBy=subDomainName
            =${search}&type=all&minExploitabilityScore=&severity=&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setLeakedCodeDetails(response.data);
          break;
        case 'dangling-subdomain':
          response = await axios.get(
            `${API_URL}/getSubDomainDetails/${orgId}/delta?page=${page}&limit=${itemsPerPage}&status=dangling&category=all&searchBy=subDomainName&name=${search}&type=all&minExploitabilityScore=&severity=&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setDanglingSubdomainDetails(response.data);
          break;
        case 'dark-web-mention':
          response = await axios.get(
            `${API_URL}/darkwebForum/${orgId}?page=${page}&limit=${itemsPerPage}&category=all&searchBy=subDomainName&name=${search}&type=all&minExploitabilityScore=&severity=&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setDarkWebMentionDetails(response.data);
          break;
        case 'postman':
          response = await axios.get(
            `${API_URL}/postmanRecord/${orgId}?page=${page}&limit=${itemsPerPage}&category=all&searchBy=subDomainName&name=${search}&type=all&minExploitabilityScore=&severity=&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setPostmanDetails(response.data);
          break;
        case 'telegram':
          response = await axios.get(
            `${API_URL}/telegram-monitoring/${orgId}/delta?page=${page}&limit=${itemsPerPage}&category=all&searchBy=subDomainName&name=${search}&type=all&minExploitabilityScore=&severity=&from=&till=`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setTelegramDetails(response.data);
          break;
        case 'spf':
          response = await axios.get(
            `${API_URL}/getDnsDetails/${orgId}?filter=true&page=${page}&limit=${itemsPerPage}&recordType=spf`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setSpfDetails(response.data);
          break;
        case 'dmarc':
          response = await axios.get(
            `${API_URL}/getDnsDetails/${orgId}?filter=true&page=${page}&limit=${itemsPerPage}&recordType=dmarc`,
            { headers: { Authorization: `Bearer ${session}` } }
          );
          setDmarcDetails(response.data);
          break;
      }

      setCurrentPage(page);
      setShowDetailsModal(true);
      setLoadingDetails(false);
    } catch (error) {
      // console.error removed
      setLoadingDetails(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setHighRiskDetails(null);
    setSslCertDetails(null);
    setVulnSslCertDetails(null);
    setObsoleteTechDetails(null);
    setStolenCredDetails(null);
    setMalwareCredDetails(null);
    setPiiDetails(null);
    setPostmanDetails(null);
    setCurrentDetailsType(null);
    setSelectedOrgId(null);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const downloadHighRiskVulnerabilities = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/cve-details/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `High Risk Vulnerabilities.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadExpiredSSLCertificates = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/expired-ssl-certificates/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Expired SSL Certificates.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadVulnerableSSLCertificates = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/expired-ssl-certificates/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Vulnerable SSL Certificates.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadVulnerableObsoleteTechnology = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/webtech_analysis/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Vulnerable Obsolete Technologies.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadStolenCredentials = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/dehashed/search/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Stolen Credentials.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadMalwareCredentials = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/getMalwareCredentials/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Malware Credentials.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadPersonalInformationBreach = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/pii-details/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Personal Information Breach.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadLeakedSessions = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/leaked-sessions/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Leaked Sessions.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadDanglingSubdomains = async (id) => {
    if (!id) return; // Add check for id
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/getSubDomainDetails/${id}/excel?status=dangling`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Dangling Subdomains.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadDarkWebMentions = async (id) => {
    if (!id) return;
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/darkwebForum/${id}/excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Dark Web Mentions.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadPostmanRecords = async (id) => {
    if (!id) return;
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/postmanRecord/${id}/excel`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Postman Records.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadTelegramRecords = async (id) => {
    if (!id) return;
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/telegram-monitoring/${id}/excel`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Telegram Records.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadLeakedCode = async (id) => {
    if (!id) return;
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/approved-code/${id}/excel`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Leaked Code.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadSpfRecords = async (id) => {
    if (!id) return;
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/getDnsDetails/${id}/excel?recordType=spf`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SPF Records.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };

  const downloadDmarcRecords = async (id) => {
    if (!id) return;
    try {
      const session = localStorage.getItem("session");
      axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;
      const response = await axios.get(
        `${API_URL}/getDnsDetails/${id}/excel?recordType=dmarc`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `DMARC Records.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error removed
    }
  };



  return (
    <div className="container-fluid p-4 dashboard-vendor">
      <div className="dashboard-header mb-4">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle text-muted">Welcome back 👋 . Here's an overview of your third-party risk management.</p>
      </div>

      {/* Action Cards */}
      {!org?.isVendorQuestionairreDisabled && (
        <div className="row mb-4">
          <div className="col-md-3">
            <Link to="/tprm/questionnaires" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="action-card">
                <div className="action-icon bg-light-blue">
                  <FontAwesomeIcon icon={faPlus} />
                </div>
                <div className="action-text">New Assessment</div>
              </div>
            </Link>
          </div>
          <div className="col-md-3">
            <Link to="/tprm/vendor#add-vendor" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="action-card">
                <div className="action-icon bg-light-green">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <div className="action-text">Add Vendor</div>
              </div>
            </Link>
          </div>
          <div className="col-md-3">
            <Link to="/tprm/trust-panel" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="action-card">
                <div className="action-icon bg-light-purple">
                  <FontAwesomeIcon icon={faFileAlt} />
                </div>
                <div className="action-text">Upload Document</div>
              </div>
            </Link>
          </div>
          <div className="col-md-3">
            <div
              className="action-card"
              onClick={handleGenerateReport}
              style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
            >
              <div className="action-icon bg-light-orange">
                {/* {loading.report ? (
                <div className="spinner-border spinner-border-sm text-white" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : ( */}
                <FontAwesomeIcon icon={faChartBar} />
                {/* )} */}
              </div>
              <div className="action-text">Generate Report</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - First Row */}
      {!org?.isVendorQuestionairreDisabled && (
        <div className="row mb-4">
          {/* Assessment Status */}
          <div className="col-md-4">
            <div className="dashboard-card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="m-0">Assessment Status</h5>
                <Link to="/tprm/assessment-campaign" className="text-primary">View All</Link>
              </div>
              <div className="card-body">
                {loading.assessments ? (
                  <div className="text-center">
                    {/* <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div> */}
                  </div>
                ) : assessmentStats ? (
                  <>
                    <Link to="/tprm/assessment-campaign?status=sent" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="total-assessments">
                        <span className="label">Total Assessments</span>
                        <span className="value">{assessmentStats.totalQuestionerForms}</span>
                      </div>
                    </Link>
                    <Link to="/tprm/assessment-campaign?status=overdue" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="overdue-assessments">
                        <span className="label">Overdue</span>
                        <span className="value text-danger">{assessmentStats.totalPastDueQuestionerForms}</span>
                      </div>
                    </Link>
                    <div className="assessment-stats">
                      <Link to="/tprm/assessment-campaign?status=unseen" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div className="stat-item">
                          <div className="stat-icon unseen">
                            <FontAwesomeIcon icon={faClock} />
                          </div>
                          <div className="stat-content">
                            <span className="stat-value">{assessmentStats.unseenQuestionerForms}</span>
                            <span className="stat-label">Unseen</span>
                          </div>
                        </div>
                      </Link>
                      <Link to="/tprm/assessment-campaign?status=in_progress" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div className="stat-item">
                          <div className="stat-icon in-progress">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                          </div>
                          <div className="stat-content">
                            <span className="stat-value">{assessmentStats.inProgressQuestionerForms}</span>
                            <span className="stat-label">In Progress</span>
                          </div>
                        </div>
                      </Link>
                      <Link to="/tprm/assessment-campaign?status=submitted" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div className="stat-item">
                          <div className="stat-icon submitted">
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </div>
                          <div className="stat-content">
                            <span className="stat-value">{assessmentStats.submittedQuestionerForms}</span>
                            <span className="stat-label">Submitted</span>
                          </div>
                        </div>
                      </Link>
                      <Link to="/tprm/assessment-campaign?status=rejected" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div className="stat-item">
                          <div className="stat-icon rejected">
                            <FontAwesomeIcon icon={faTimesCircle} />
                          </div>
                          <div className="stat-content">
                            <span className="stat-value">{assessmentStats.totalRejectedQuestionerForms}</span>
                            <span className="stat-label">Rejected</span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted">
                    No assessment data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Risk Overview */}
          <div className="col-md-4">
            <div className="dashboard-card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="m-0">Risk Overview</h5>
                <Link to="/tprm/risk-register" className="text-primary">View All</Link>
              </div>
              <div className="card-body">
                {loading.riskOverview ? (
                  <div className="text-center">
                    {/* <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div> */}
                  </div>
                ) : riskOverview ? (
                  <div className="risk-badges-container">
                    <Link to={`/tprm/risk-register?risk=high`} className="risk-badge-block high-block">
                      <div className="risk-number">{riskOverview.data?.totalHigh}</div>
                      <div className="risk-label">High</div>
                    </Link>
                    <Link to={`/tprm/risk-register?risk=medium`} className="risk-badge-block medium-block">
                      <div className="risk-number">{riskOverview.data?.totalMedium}</div>
                      <div className="risk-label">Medium</div>
                    </Link>
                    <Link to={`/tprm/risk-register?risk=low`} className="risk-badge-block low-block">
                      <div className="risk-number">{riskOverview.data?.totalLow}</div>
                      <div className="risk-label">Low</div>
                    </Link>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    No risk data available
                  </div>
                )}
                <Link to="/tprm/risk-register" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <button className="btn btn-light btn-block mt-3">View Details</button>
                </Link>
              </div>
            </div>
          </div>

          {/* Pending Reviews */}
          <div className="col-md-4">
            <div className="dashboard-card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="m-0">Pending Reviews</h5>
                <Link to="/tprm/assessment-campaign" className="text-primary">View All</Link>
              </div>
              <div className="card-body p-0">
                {loading.reviews ? (
                  <div className="text-center p-4">
                    {/* <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div> */}
                  </div>
                ) : pendingReviews.length > 0 ? (
                  <ul className="list-group list-group-flush">
                    {pendingReviews.map(review => (
                      <li
                        key={review._id}
                        className="list-group-item d-flex align-items-center"
                        onClick={() => window.location.href = `/tprm/review-risk/${review._id}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="review-avatar">
                          {review.logoUrl ? (
                            <img src={review.logoUrl} alt="Company Logo" className="company-logo" />
                          ) : (
                            <FontAwesomeIcon icon={faBuildingDefault} />
                          )}
                        </div>
                        <div className="review-details flex-grow-1">
                          <div className="review-name">{review.formTitle}</div>
                          <div className="review-meta">
                            <span className="review-org">{review.assigneeOrganisation}</span>
                            <span className="review-date">Due: {formatDate(review.dueDate)}</span>
                          </div>
                        </div>
                        <span className={getStatusBadgeClass(review.status)}>
                          {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center p-4 text-muted">
                    No pending reviews
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Findings Section */}
      {vendorFindings && (vendorFindings.attackSurface?.length > 0 || vendorFindings.dataExposure?.length > 0) && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="dashboard-card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="m-0">Continuous Monitoring Findings</h5>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                    Real-time security vulnerabilities and data exposure risks detected across your vendor ecosystem.
                    Based on attack vectors, you can see which vendors are affected and drill down to view detailed findings for each organization.
                  </p>
                </div>
                {/* <Link to="/vendor-findings" className="text-primary">View All</Link> */}
              </div>
              <div className="card-body">
                {loading.findings ? (
                  <div className="text-center">
                    {/* <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div> */}
                  </div>
                ) : (
                  <div className="row">
                    {/* Attack Surface Section */}
                    {vendorFindings.attackSurface?.length > 0 && (
                      <div className="col-md-6">
                        <h6 className="section-title mb-3">Attack Surface</h6>
                        <div className="findings-list">
                          {vendorFindings.attackSurface.map((finding, index) => (
                            <div key={index} className="finding-card">
                              <div className="finding-icon">
                                <FontAwesomeIcon icon={getModuleIcon(finding.module)} />
                              </div>
                              <div className="finding-content">
                                <h3>{extractNumber(finding.risk)}</h3>
                                <p>{finding.moduleName}</p>
                                <small className="text-muted">{finding.risk}</small>
                                <div className="mt-2">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleViewCompanies(finding)}
                                  >
                                    View Affected Vendors
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Data Exposure Section */}
                    {vendorFindings.dataExposure?.length > 0 && (
                      <div className="col-md-6">
                        <h6 className="section-title mb-3">Data Exposure</h6>
                        <div className="findings-list">
                          {vendorFindings.dataExposure.map((finding, index) => (
                            <div key={index} className="finding-card">
                              <div className="finding-icon">
                                <FontAwesomeIcon icon={getModuleIcon(finding.module)} />
                              </div>
                              <div className="finding-content">
                                <h3>{extractNumber(finding.risk)}</h3>
                                <p>{finding.moduleName}</p>
                                <small className="text-muted">{finding.risk}</small>
                                <div className="mt-2">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleViewCompanies(finding)}
                                  >
                                    View Affected Vendors
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Second Row */}
      <div className="row">
      </div>

      {/* Company List Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <div className="d-flex align-items-center">
                  <div className="finding-icon me-3">
                    <FontAwesomeIcon icon={getModuleIcon(selectedFinding?.module)} size="2x" />
                  </div>
                  <div>
                    <h5 className="modal-title mb-1">{selectedFinding?.moduleName}</h5>
                    <p className="text-muted mb-0">Risk Level: {selectedFinding?.risk}</p>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body p-0">
                {loading.companies ? (
                  <div className="text-center p-5">
                    {/* <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div> */}
                    {/* <p className="mt-3 text-muted">Loading affected companies...</p> */}
                  </div>
                ) : companyLists[selectedFinding?.module] ? (
                  <div className="affected-companies-list">
                    {/* Summary Section */}
                    <div className="bg-light p-4 border-bottom">
                      <div className="row">
                        <div className="col-md-3">
                          <div className="summary-card bg-white p-3 rounded shadow-sm">
                            <h6 className="text-muted mb-2">Total Affected Companies</h6>
                            <h3 className="mb-0">{companyLists[selectedFinding?.module].length}</h3>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="summary-card bg-white p-3 rounded shadow-sm">
                            <h6 className="text-muted mb-2">Total Instances</h6>
                            <h3 className="mb-0">
                              {companyLists[selectedFinding?.module].reduce((sum, company) => sum + company.numberOfInstances, 0)}
                            </h3>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="summary-card bg-white p-3 rounded shadow-sm">
                            <h6 className="text-muted mb-2">High Risk Companies</h6>
                            <h3 className="mb-0 text-danger">
                              {companyLists[selectedFinding?.module].filter(company => company.numberOfInstances > 5).length}
                            </h3>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="summary-card bg-white p-3 rounded shadow-sm">
                            <h6 className="text-muted mb-2">Average Instances</h6>
                            <h3 className="mb-0">
                              {(companyLists[selectedFinding?.module].reduce((sum, company) => sum + company.numberOfInstances, 0) /
                                companyLists[selectedFinding?.module].length).toFixed(1)}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Companies Table */}
                    <div className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6 className="mb-0">Affected Companies List</h6>
                        <div className="d-flex gap-2">
                          <div className="input-group searchBoxInput p-0">
                            <input type="text" className="form-control" placeholder="Search companies..." />
                          </div>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle">
                          <thead className="bg-light">
                            <tr>
                              <th style={{ width: '30%' }}>Organization</th>
                              <th style={{ width: '15%' }}>Risk Level</th>
                              <th style={{ width: '15%' }}>Instances</th>
                              <th style={{ width: '20%' }}>Last Updated</th>
                              <th style={{ width: '20%' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companyLists[selectedFinding?.module].map((company, idx) => (
                              <tr key={idx}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="company-avatar me-3 bg-light rounded-circle p-2">
                                      <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                                    </div>
                                    <div>
                                      <div className="fw-bold">{company.orgName}</div>
                                      <div className="d-flex gap-2 mt-1">
                                        <span className="badge bg-light text-dark">ID: {company._id}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className={`risk-indicator me-2 ${company.numberOfInstances > 5 ? 'high' :
                                      company.numberOfInstances > 2 ? 'medium' : 'low'
                                      }`}></div>
                                    <span className={`badge ${company.numberOfInstances > 5 ? 'bg-danger' :
                                      company.numberOfInstances > 2 ? 'bg-warning' : 'bg-success'
                                      } rounded-pill`}>
                                      {company.numberOfInstances > 5 ? 'High' :
                                        company.numberOfInstances > 2 ? 'Medium' : 'Low'}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="progress flex-grow-1 me-2" style={{ height: '6px' }}>
                                      <div
                                        className={`progress-bar ${company.numberOfInstances > 5 ? 'bg-danger' :
                                          company.numberOfInstances > 2 ? 'bg-warning' : 'bg-success'
                                          }`}
                                        role="progressbar"
                                        style={{ width: `${(company.numberOfInstances / 10) * 100}%` }}
                                      ></div>
                                    </div>
                                    <span className="fw-bold">{company.numberOfInstances}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faClock} className="text-muted me-2" />
                                    <span>Last 24 hours</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleViewDetails(company._id, selectedFinding.module)}
                                      disabled={loadingDetails}
                                    >
                                      {/* {loadingDetails ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                          Loading...
                                        </>
                                      ) : ( */}
                                      <>
                                        <FontAwesomeIcon icon={faEye} className="mr-1" />
                                        View Details
                                      </>
                                      {/* )} */}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-5">
                    <div className="empty-state">
                      <FontAwesomeIcon icon={faBuilding} size="3x" className="text-muted mb-3" />
                      <h5>No Affected Companies</h5>
                      <p className="text-muted">No companies are currently affected by this finding.</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <div className="d-flex justify-content-between w-100">
                  <div>
                    <span className="text-muted me-3">
                      Last Updated: {new Date().toLocaleString()}
                    </span>
                    <span className="text-muted ml-2">
                      Total Records: {companyLists[selectedFinding?.module]?.length || 0}
                    </span>
                  </div>
                  <div>
                    <button type="button" className="btn btn-round btn-default me-2" onClick={closeModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CVE Details Modal */}
      {showCveModal && cveDetails && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title">CVE Details</h5>
                <button type="button" className="btn-close" onClick={closeCveModal}></button>
              </div>
              <div className="modal-body p-0">
                <div className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0">Total Records: {cveDetails.data.cveDetailsRecord}</h6>
                      <small className="text-muted">Page {cveDetails.data.currentPage} of {cveDetails.data.totalPages}</small>
                    </div>
                    <div className="d-flex gap-2">
                      <div className="input-group searchBoxInput p-0">
                        <input type="text" className="form-control" placeholder="Search CVE..." />
                      </div>
                      <button className="btn btn-outline-primary">
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Export
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>CVE ID</th>
                          <th>Severity</th>
                          <th>IP Address</th>
                          <th>Published Date</th>
                          <th>Known Exploit</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cveDetails.data.data.map((cve) => (
                          <tr key={cve._id}>
                            <td>
                              <div className="fw-bold">{cve.cveId}</div>
                              <small className="text-muted d-block" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cve.description}
                              </small>
                            </td>
                            <td>
                              <span className={`badge ${cve.severity === 'CRITICAL' ? 'bg-danger' :
                                cve.severity === 'HIGH' ? 'bg-warning' :
                                  cve.severity === 'MEDIUM' ? 'bg-info' : 'bg-success'
                                }`}>
                                {cve.severity}
                              </span>
                            </td>
                            <td>{cve.ip}</td>
                            <td>{new Date(cve.cvePublishedAt).toLocaleDateString()}</td>
                            <td>
                              {cve.knownExploit ? (
                                <span className="badge bg-danger">Yes</span>
                              ) : (
                                <span className="badge bg-success">No</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-primary">
                                  <FontAwesomeIcon icon={faEye} className="me-1" />
                                  Details
                                </button>
                                {cve.exploitUrl.length > 0 && (
                                  <button className="btn btn-sm btn-outline-danger">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                    Exploit
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, cveDetails.data.cveDetailsRecord)} of {cveDetails.data.cveDetailsRecord} entries
                  </div>
                  <div className="dataTables_paginate">
                    {cveDetails.data.cveDetailsRecord > 0 && (
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={cveDetails.data.cveDetailsRecord}
                        pageSize={itemsPerPage}
                        onPageChange={(page) => handlePageChange(page)}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <div className="d-flex justify-content-between w-100">
                  <div>
                    <span className="text-muted">
                      Last Updated: {new Date().toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <button type="button" className="btn btn-round btn-default me-2" onClick={closeCveModal}>
                      Close
                    </button>
                    <button type="button" className="btn btn-primary">
                      <FontAwesomeIcon icon={faDownload} className="mr-2" style={{ display: 'inline-block' }} />
                      Export Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title">
                  {currentDetailsType === 'high-risk-vulnerability' && 'High Risk Vulnerabilities'}
                  {currentDetailsType === 'expired-ssl-certificate' && 'Expired SSL Certificates'}
                  {currentDetailsType === 'vulnerable-ssl-certificate' && 'Vulnerable SSL Certificates'}
                  {currentDetailsType === 'vulnerable-obsolete-technology' && 'Vulnerable Obsolete Technologies'}
                  {currentDetailsType === 'stolen-cred' && 'Stolen Credentials'}
                  {currentDetailsType === 'malware-cred' && 'Malware Credentials'}
                  {currentDetailsType === 'personal-information-breach' && 'Personal Information Breach'}
                  {currentDetailsType === 'postman' && 'Postman Monitoring'}
                  {currentDetailsType === 'spf' && 'SPF Records'}
                  {currentDetailsType === 'dmarc' && 'DMARC Records'}
                </h5>
                <button type="button" className="btn-close" onClick={closeDetailsModal}></button>
              </div>
              <div className="modal-body p-0">
                <div className="p-4">
                  <div className="details-header d-flex justify-content-between align-items-center">
                    <div>
                      {currentDetailsType === 'high-risk-vulnerability' && 'High Risk Vulnerabilities'}
                      {currentDetailsType === 'expired-ssl-certificate' && 'Expired SSL Certificates'}
                      {currentDetailsType === 'vulnerable-ssl-certificate' && 'Vulnerable SSL Certificates'}
                      {currentDetailsType === 'vulnerable-obsolete-technology' && 'Vulnerable Obsolete Technologies'}
                      {currentDetailsType === 'stolen-cred' && 'Stolen Credentials'}
                      {currentDetailsType === 'malware-cred' && 'Malware Credentials'}
                      {currentDetailsType === 'personal-information-breach' && 'Personal Information Breach'}
                      {currentDetailsType === 'postman' && 'Postman Monitoring'}
                      {currentDetailsType === 'spf' && 'SPF Records'}
                      {currentDetailsType === 'dmarc' && 'DMARC Records'}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0">
                        Total Records: {
                          currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.cveDetailsRecord ||
                          currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.totalResults ||
                          currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.totalResults ||
                          currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.totalTechs ||
                          currentDetailsType === 'stolen-cred' && stolenCredDetails?.data?.totalLeakedCredentials ||
                          currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.totalMalwareCredentials ||
                          currentDetailsType === 'personal-information-breach' && piiDetails?.data?.totalPersonalInformationLeakage ||
                          currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.totalLeakedSessions ||
                          currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.totalDanglingSubdomains ||
                          currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.data?.totalCount ||
                          currentDetailsType === 'postman' && postmanDetails?.totalCount ||
                          currentDetailsType === 'telegram' && telegramDetails?.data?.totalRecords ||
                          currentDetailsType === 'spf' && spfDetails?.totalDocs ||
                          currentDetailsType === 'dmarc' && dmarcDetails?.totalDocs
                        }
                      </h6>
                      <small className="text-muted">
                        Page {
                          currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.currentPage ||
                          currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.page ||
                          currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.page ||
                          currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.currentPage ||
                          currentDetailsType === 'personal-information-breach' && piiDetails?.data?.currentPage ||
                          currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.currentPage ||
                          currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.currentPage ||
                          currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.currentPage ||
                          currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.data?.currentPage ||
                          currentDetailsType === 'postman' && postmanDetails?.currentPage ||
                          currentDetailsType === 'telegram' && telegramDetails?.data?.currentPage ||
                          currentDetailsType === 'spf' && spfDetails?.currentPage ||
                          currentDetailsType === 'dmarc' && dmarcDetails?.currentPage
                        } of {
                          currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.totalPages ||
                          currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.totalPages ||
                          currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.totalPages ||
                          currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.totalPages ||
                          currentDetailsType === 'personal-information-breach' && piiDetails?.data?.totalPages ||
                          currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.totalPages ||
                          currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.totalPages ||
                          currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.totalPages ||
                          currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.totalPages ||
                          currentDetailsType === 'postman' && postmanDetails?.totalPages ||
                          currentDetailsType === 'telegram' && telegramDetails?.data?.totalPages ||
                          currentDetailsType === 'spf' && spfDetails?.totalPages ||
                          currentDetailsType === 'dmarc' && dmarcDetails?.totalPages
                        }
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <div className="input-group searchBoxInput p-0">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={
                            currentDetailsType === 'high-risk-vulnerability' ? "Search CVE..." :
                              currentDetailsType === 'expired-ssl-certificate' ? "Search domains..." :
                                currentDetailsType === 'vulnerable-ssl-certificate' ? "Search domains..." :
                                  currentDetailsType === 'vulnerable-obsolete-technology' ? "Search technologies..." :
                                    currentDetailsType === 'stolen-cred' ? "Search emails..." :
                                      currentDetailsType === 'personal-information-breach' ? "Search emails..." :
                                        "Search query..."
                          }
                          value={searchTerm}
                          onChange={handleSearch}
                        />
                      </div>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                          if (!selectedOrgId) return; // Add check for selectedOrgId
                          switch (currentDetailsType) {
                            case 'high-risk-vulnerability':
                              downloadHighRiskVulnerabilities(selectedOrgId);
                              break;
                            case 'expired-ssl-certificate':
                              downloadExpiredSSLCertificates(selectedOrgId);
                              break;
                            case 'vulnerable-ssl-certificate':
                              downloadVulnerableSSLCertificates(selectedOrgId);
                              break;
                            case 'vulnerable-obsolete-technology':
                              downloadVulnerableObsoleteTechnology(selectedOrgId);
                              break;
                            case 'stolen-cred':
                              downloadStolenCredentials(selectedOrgId);
                              break;
                            case 'malware-cred':
                              downloadMalwareCredentials(selectedOrgId);
                              break;
                            case 'personal-information-breach':
                              downloadPersonalInformationBreach(selectedOrgId);
                              break;
                            case 'leaked-session':
                              downloadLeakedSessions(selectedOrgId);
                              break;
                            case 'dangling-subdomain':
                              downloadDanglingSubdomains(selectedOrgId);
                              break;
                            case 'dark-web-mention':
                              downloadDarkWebMentions(selectedOrgId);
                              break;
                            case 'postman':
                              downloadPostmanRecords(selectedOrgId);
                              break;
                            case 'telegram':
                              downloadTelegramRecords(selectedOrgId);
                              break;
                            case 'leaked-code':
                              downloadLeakedCode(selectedOrgId);
                              break;
                            case 'dmarc':
                              downloadDmarcRecords(selectedOrgId);
                              break;
                            case 'spf':
                              downloadSpfRecords(selectedOrgId);
                              break;
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" style={{ display: 'inline-block' }} />
                        Export
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      {currentDetailsType === 'high-risk-vulnerability' && (
                        <thead className="bg-light">
                          <tr>
                            <th>CVE ID</th>
                            <th>Severity</th>
                            <th>IP Address</th>
                            <th>Published Date</th>
                            <th>Known Exploit</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                      )}
                      {(currentDetailsType === 'expired-ssl-certificate' || currentDetailsType === 'vulnerable-ssl-certificate') && (
                        <thead className="bg-light">
                          <tr>
                            <th>Domain</th>
                            <th>Status</th>
                            <th>Expiration</th>
                            <th>Certificate Details</th>
                            <th>Weak Ciphers</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'vulnerable-obsolete-technology' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Subdomain</th>
                            <th>Technology</th>
                            <th>Version</th>
                            <th>Latest Version</th>
                            <th>Highest Severity</th>
                            <th>Highest CVSS</th>
                            <th>Highest Exploitability</th>
                            <th>CVEs</th>
                            <th>Outdated</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'stolen-cred' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Email</th>
                            <th>Password</th>
                            <th>Source</th>
                            <th>Type</th>
                            <th>Created At</th>
                            <th>Updated At</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'malware-cred' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Username</th>
                            <th>Password</th>
                            <th>Source URL</th>
                            <th>First Seen</th>
                            <th>Created At</th>
                            <th>Updated At</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'personal-information-breach' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Source</th>
                            <th>Email</th>
                            <th>Username</th>
                            <th>Phone</th>
                            <th>IP Address</th>
                            <th>Address</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'leaked-session' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Name</th>
                            <th>Domain</th>
                            <th>Value</th>
                            <th>Path</th>
                            <th>Session Expiry</th>
                            <th>Found On</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'dangling-subdomain' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Subdomain</th>
                            <th>IP Address</th>
                            <th>Status</th>
                            <th>Type</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'dark-web-mention' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Title</th>
                            <th>Author Name</th>
                            <th>Date</th>
                            <th>Screenshot Path</th>
                            <th>Source</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'telegram' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Message</th>
                            <th>Group Name</th>
                            <th>Link</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'spf' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Domain</th>
                            <th>Record Type</th>
                            <th>Record Value</th>
                            <th>Observation</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'dmarc' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Domain</th>
                            <th>Record Type</th>
                            <th>Record Value</th>
                            <th>Observation</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                      )}
                      {currentDetailsType === 'leaked-code' && (
                        <thead className="bg-light">
                          <tr>
                            <th>Repository</th>
                            <th>Created At</th>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.data.map((cve) => (
                          <tr key={cve._id}>
                            <td>
                              <div className="fw-bold">{cve.cveId}</div>
                              <small className="text-muted d-block" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cve.description}
                              </small>
                            </td>
                            <td>
                              <span className={`badge ${cve.severity === 'CRITICAL' ? 'bg-danger' :
                                cve.severity === 'HIGH' ? 'bg-warning' :
                                  cve.severity === 'MEDIUM' ? 'bg-info' : 'bg-success'
                                }`}>
                                {cve.severity}
                              </span>
                            </td>
                            <td>{cve.ip}</td>
                            <td>{new Date(cve.cvePublishedAt).toLocaleDateString()}</td>
                            <td>
                              {cve.knownExploit ? (
                                <span className="badge bg-danger">Yes</span>
                              ) : (
                                <span className="badge bg-success">No</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {cve.exploitUrl.length > 0 && (
                                  <button className="btn btn-sm btn-outline-danger">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                    Exploit
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {(currentDetailsType === 'expired-ssl-certificate' || currentDetailsType === 'vulnerable-ssl-certificate') &&
                          (currentDetailsType === 'expired-ssl-certificate' ? sslCertDetails?.data : vulnSslCertDetails?.data).map((cert) => (
                            <tr key={cert?._id}>
                              <td>
                                <div className="fw-bold">{cert?.subDomainName}</div>
                                <small className="text-muted">Serial: {cert?.certificateSerialNumber}</small>
                              </td>
                              <td>
                                <span className={`badge ${cert?.status === 'Expired' ? 'bg-danger' : 'bg-warning'
                                  }`}>
                                  {cert?.status}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon
                                    icon={cert.expiresInDays < 0 ? faTimesCircle : faClock}
                                    className={`me-2 ${cert.expiresInDays < 0 ? 'text-danger' : 'text-warning'}`}
                                  />
                                  <span>{Math.abs(cert?.expiresInDays)} days {cert?.expiresInDays < 0 ? 'ago' : 'remaining'}</span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  {cert?.isRevokedCertificate && (
                                    <span className="badge bg-danger">Revoked</span>
                                  )}
                                  {cert?.isMismatchedCertificate && (
                                    <span className="badge bg-warning">Mismatched</span>
                                  )}
                                  {cert?.isSelfSignedCertificate && (
                                    <span className="badge bg-info">Self-Signed</span>
                                  )}
                                  {cert?.isUntrustedCertificate && (
                                    <span className="badge bg-danger">Untrusted</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                {cert?.weakCiphers && cert?.weakCiphers?.length > 0 ? (
                                  <div className="d-flex flex-column gap-1">
                                    {cert?.weakCiphers?.map((cipher, idx) => (
                                      <div key={idx}>
                                        <small className="text-muted d-block mb-1">{cipher?.k}</small>
                                        <div className="d-flex flex-wrap gap-1">
                                          {cipher?.v?.slice(0, 3).map((c, i) => (
                                            <span key={i} className="badge bg-warning">{c}</span>
                                          ))}
                                          {cipher?.v?.length > 3 && (
                                            <span className="badge bg-secondary">+{cipher?.v?.length - 3} more</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="badge bg-success">None</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <small>Updated: {new Date(cert?.updatedAt).toLocaleDateString()}</small>
                                  <small className="text-muted">Created: {new Date(cert?.createdAt).toLocaleDateString()}</small>
                                </div>
                              </td>
                            </tr>
                          ))}
                        {currentDetailsType === 'vulnerable-obsolete-technology' && Array.isArray(obsoleteTechDetails?.data?.technologies) && obsoleteTechDetails?.data?.technologies.map((tech) => (

                          <tr key={tech?._id}>
                            <td>{tech?.subDomainName}</td>
                            <td>{tech?.name}</td>
                            <td>{tech?.version}</td>
                            <td>{tech?.latestVersion}</td>
                            <td>
                              <span className={`badge ${tech?.highestSeverity === 'CRITICAL' ? 'bg-danger' :
                                tech?.highestSeverity === 'HIGH' ? 'bg-warning' :
                                  tech?.highestSeverity === 'MEDIUM' ? 'bg-info' : 'bg-success'
                                }`}>
                                {tech?.highestSeverity}
                              </span>
                            </td>
                            <td>{tech?.highestCvssScore}</td>
                            <td>{tech?.highestExploitability}</td>
                            <td>
                              {tech?.cvesInfo && tech?.cvesInfo?.length > 0 ? (
                                <div className="d-flex flex-column gap-1">
                                  {tech?.cvesInfo?.map((cve, idx) => (
                                    <div key={idx}>
                                      <span className="fw-bold">{cve?.cveId}</span>
                                      {cve?.knownExploits && cve?.knownExploits?.length > 0 && (
                                        <>
                                          {cve?.knownExploits?.map((url, i) => (
                                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="badge bg-danger ms-2">Exploit</a>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="badge bg-success">None</span>
                              )}
                            </td>
                            <td>
                              {tech?.outdated ? (
                                <span className="badge bg-warning">Yes</span>
                              ) : (
                                <span className="badge bg-success">No</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <small>Updated: {new Date(tech?.updatedAt).toLocaleDateString()}</small>
                                <small className="text-muted">Created: {new Date(tech?.createdAt).toLocaleDateString()}</small>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {currentDetailsType === 'stolen-cred' && Array.isArray(stolenCredDetails?.data?.data) && stolenCredDetails.data.data.length > 0 ? (
                          stolenCredDetails.data.data.map((row) => (
                            <tr key={row._id}>
                              <td>{row.eml}</td>
                              <td><span style={{ fontFamily: 'monospace' }}>{row.pwd}</span></td>
                              <td>{row.src}</td>
                              <td>{row.type}</td>
                              <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ''}</td>
                              <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : ''}</td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'stolen-cred' ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">No credentials found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'malware-cred' && Array.isArray(malwareCredDetails?.data?.data) && malwareCredDetails.data.data.length > 0 ? (
                          malwareCredDetails.data.data.map((row) => (
                            <tr key={row._id}>
                              <td>{row.usr}</td>
                              <td><span style={{ fontFamily: 'monospace' }}>{row.pwd}</span></td>
                              <td>{row.src ? (<a href={row.src} target="_blank" rel="noopener noreferrer">{row.src}</a>) : ''}</td>
                              <td>{row.fnd ? new Date(row.fnd).toLocaleDateString() : ''}</td>
                              <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ''}</td>
                              <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : ''}</td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'malware-cred' ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted">No malware credentials found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'personal-information-breach' && Array.isArray(piiDetails?.data?.data) && piiDetails?.data?.data?.length > 0 ? (
                          piiDetails?.data?.data?.map((pii) => (
                            <tr key={pii?._id}>
                              <td>
                                <span className="badge bg-info">{pii?.database_name || 'Unknown'}</span>
                              </td>
                              <td>{pii?.email || '*****'}</td>
                              <td>{pii?.username || '*****'}</td>
                              <td>{pii?.phone || '*****'}</td>
                              <td>{pii?.ip_address || '*****'}</td>
                              <td>{pii?.address || '*****'}</td>
                              <td>{pii?.createdAt ? new Date(pii.createdAt).toLocaleDateString() : ''}</td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'personal-information-breach' ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted">No personal information breach found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'leaked-session' && Array.isArray(leakedSessionDetails?.data?.data) && leakedSessionDetails?.data?.data?.length > 0 ? (
                          leakedSessionDetails?.data?.data?.map((session) => (
                            <tr key={session?._id}>
                              <td>
                                <div className="fw-bold">{session?.name}</div>
                              </td>
                              <td>
                                <span style={{ fontFamily: 'monospace' }}>{session?.dom}</span>
                              </td>
                              <td>
                                <span>
                                  {session?.val}
                                </span>
                              </td>
                              <td>
                                <span style={{ fontFamily: 'monospace' }}>
                                  {session?.path}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  {new Date(session?.expires)?.toLocaleDateString()}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <span style={{ fontFamily: 'monospace' }}>
                                    {new Date(session?.updatedAt)?.toLocaleDateString()}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'leaked-session' ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">No leaked sessions found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'leaked-code' && Array.isArray(leakedCodeDetails?.data?.data) && leakedCodeDetails?.data?.data?.length > 0 ? (
                          leakedCodeDetails?.data?.data?.map((code) => (
                            <tr key={code?._id}>
                              <td>{code?.repoUrl}</td>
                              <td>{new Date(code?.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'leaked-code' ? (
                          <tr>
                            <td colSpan={3} className="text-center text-muted">No leaked code found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'dangling-subdomain' && Array.isArray(danglingSubdomainDetails?.data?.data) && danglingSubdomainDetails?.data?.data?.length > 0 ? (
                          danglingSubdomainDetails?.data?.data?.map((domain) => (
                            <tr key={domain?._id}>
                              <td>
                                <div className="fw-bold">{domain?.subDomainName}</div>
                              </td>
                              <td>
                                <span style={{ fontFamily: 'monospace' }}>{domain?.ip}</span>
                              </td>
                              <td>
                                <span className={`badge ${domain?.status === 'dangling' ? 'bg-danger' : 'bg-warning'}`}>
                                  {domain?.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${domain?.type === 'CNAME' ? 'bg-info' : 'bg-secondary'}`}>
                                  {domain?.isDangling ? 'Dangling' : 'Not Dangling'}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <small>Updated: {new Date(domain?.updatedAt).toLocaleDateString()}</small>
                                  <small className="text-muted">Created: {new Date(domain?.createdAt).toLocaleDateString()}</small>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'dangling-subdomain' ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">No dangling subdomains found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'dark-web-mention' && Array.isArray(darkWebMentionDetails?.data) && darkWebMentionDetails?.data?.length > 0 ? (
                          darkWebMentionDetails?.data?.map((mention) => (
                            <tr key={mention?._id}>
                              <td>
                                <div className="text-truncate" style={{ maxWidth: '200px' }} title={mention?.title}>
                                  {mention?.title}
                                </div>
                              </td>
                              <td>
                                <div className="fw-bold">{mention?.author_name}</div>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <small>{new Date(mention?.date).toLocaleDateString()}</small>
                                  <small className="text-muted">{new Date(mention?.updatedAt).toLocaleTimeString()}</small>
                                </div>
                              </td>
                              <td>
                                <a href={mention?.fixed_screenshot_path} target="_blank" rel="noopener noreferrer" className="text-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                  View Screenshot
                                </a>
                              </td>
                              <td>
                                <a href={mention?.thread_link} target="_blank" rel="noopener noreferrer" className="text-primary">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                  {mention?.source}
                                </a>
                              </td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'dark-web-mention' ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">No dark web mentions found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'telegram' && Array.isArray(telegramDetails?.data?.data) && telegramDetails?.data?.data?.length > 0 ? (
                          telegramDetails?.data?.data?.map((telegram) => (
                            <tr key={telegram?._id}>
                              <td>
                                {telegram?.message && (
                                  <div>
                                    {telegram.message.length > 100 ? (
                                      <div>
                                        <span>{telegram.message.substring(0, 100)}...</span>
                                        <button
                                          onClick={() => {
                                            const element = document.getElementById(`message-${telegram.id}`);
                                            if (element) {
                                              element.style.display = element.style.display === 'none' ? 'inline' : 'none';
                                            }
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#6563ff',
                                            cursor: 'pointer',
                                            padding: '0 5px'
                                          }}
                                        >
                                          show more
                                        </button>
                                        <span id={`message-${telegram.id}`} style={{ display: 'none' }}>
                                          {telegram.message}
                                          <button
                                            onClick={() => {
                                              const element = document.getElementById(`message-${telegram.id}`);
                                              if (element) {
                                                element.style.display = 'none';
                                              }
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              color: '#6563ff',
                                              cursor: 'pointer',
                                              padding: '0 5px'
                                            }}
                                          >
                                            show less
                                          </button>
                                        </span>
                                      </div>
                                    ) : (
                                      telegram.message
                                    )}
                                  </div>
                                )}
                              </td>
                              <td>{telegram?.group_name}</td>
                              <td>{telegram?.message_link}</td>
                              <td>{telegram?.updatedAt}</td>
                            </tr>
                          ))
                        ) : currentDetailsType === 'telegram' ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted">No telegram records found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'spf' && Array.isArray(spfDetails?.data) && spfDetails?.data?.length > 0 ? (spfDetails?.data?.map((spf) => (
                          <tr key={spf?._id}>
                            <td>{spf?.subDomain}</td>
                            <td>{spf?.recordType}</td>
                            <td>{spf?.record}</td>
                            <td>{spf?.error}</td>
                            <td>{spf?.updatedAt}</td>
                          </tr>
                        ))
                        ) : currentDetailsType === 'spf' ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">No SPF records found.</td>
                          </tr>
                        ) : null}
                        {currentDetailsType === 'dmarc' && Array.isArray(dmarcDetails?.data) && dmarcDetails?.data?.length > 0 ? (dmarcDetails?.data?.map((dmarc) => (
                          <tr key={dmarc?._id}>
                            <td>{dmarc?.subDomain}</td>
                            <td>{dmarc?.recordType}</td>
                            <td>{dmarc?.record}</td>
                            <td>{dmarc?.error}</td>
                            <td>{dmarc?.updatedAt}</td>
                          </tr>
                        ))
                        ) : currentDetailsType === 'dmarc' ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">No DMARC records found.</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                  {loadingDetails ? (
                    <div className="text-center p-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading details...</p>
                    </div>
                  ) : (
                    <div className="table-responsive mt-4">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            {currentDetailsType === 'postman' && (
                              <>
                                <th>Workspace URL</th>
                                <th>User detail</th>
                                <th>Updated At</th>
                              </>
                            )}
                            {/* Add other cases here */}
                          </tr>
                        </thead>
                        <tbody>
                          {currentDetailsType === 'postman' && postmanDetails?.data?.map((item, index) => (
                            <tr key={index}>
                              <td>{item?.url}</td>
                              <td>{item?.user_details}</td>
                              <td>{item?.createdAt}</td>
                            </tr>
                          ))}
                          {/* Add other cases here */}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {
                    Math.min(
                      currentPage * itemsPerPage,
                      currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.cveDetailsRecord ||
                      currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.totalResults ||
                      currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.totalResults ||
                      currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.totalTechs ||
                      currentDetailsType === 'stolen-cred' && stolenCredDetails?.data?.totalLeakedCredentials ||
                      currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.totalMalwareCredentials ||
                      currentDetailsType === 'personal-information-breach' && piiDetails?.data?.totalPersonalInformationLeakage ||
                      currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.totalLeakedSessions ||
                      currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.totalDomains ||
                      currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.totalCount ||
                      currentDetailsType === 'postman' && postmanDetails?.totalCount ||
                      currentDetailsType === 'telegram' && telegramDetails?.data?.totalRecords ||
                      currentDetailsType === 'spf' && spfDetails?.totalDocs ||
                      currentDetailsType === 'dmarc' && dmarcDetails?.totalDocs ||
                      currentDetailsType === 'leaked-code' && leakedCodeDetails?.data?.totalLeakedCodes
                    )
                  } of {
                    currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.cveDetailsRecord ||
                    currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.totalResults ||
                    currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.totalResults ||
                    currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.totalTechs ||
                    currentDetailsType === 'stolen-cred' && stolenCredDetails?.data?.totalLeakedCredentials ||
                    currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.totalMalwareCredentials ||
                    currentDetailsType === 'personal-information-breach' && piiDetails?.data?.totalPersonalInformationLeakage ||
                    currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.totalLeakedSessions ||
                    currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.totalDomains ||
                    currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.totalCount ||
                    currentDetailsType === 'postman' && postmanDetails?.totalCount ||
                    currentDetailsType === 'telegram' && telegramDetails?.data?.totalRecords ||
                    currentDetailsType === 'spf' && spfDetails?.totalDocs ||
                    currentDetailsType === 'dmarc' && dmarcDetails?.totalDocs ||
                    currentDetailsType === 'leaked-code' && leakedCodeDetails?.data?.totalLeakedCodes
                  } entries
                </div>
                <div className="dataTables_paginate">
                  {(
                    (currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.cveDetailsRecord) ||
                    (currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.totalResults) ||
                    (currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.totalResults) ||
                    (currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.totalTechs) ||
                    (currentDetailsType === 'stolen-cred' && stolenCredDetails?.data?.totalLeakedCredentials) ||
                    (currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.totalMalwareCredentials) ||
                    (currentDetailsType === 'personal-information-breach' && piiDetails?.data?.totalPersonalInformationLeakage) ||
                    (currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.totalLeakedSessions) ||
                    (currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.totalDomains) ||
                    (currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.totalCount) ||
                    (currentDetailsType === 'postman' && postmanDetails?.totalCount) ||
                    (currentDetailsType === 'telegram' && telegramDetails?.data?.totalRecords) ||
                    (currentDetailsType === 'spf' && spfDetails?.totalDocs) ||
                    (currentDetailsType === 'dmarc' && dmarcDetails?.totalDocs) ||
                    (currentDetailsType === 'leaked-code' && leakedCodeDetails?.data?.totalLeakedCodes)
                  ) > 0 && (
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={
                          currentDetailsType === 'high-risk-vulnerability' && highRiskDetails?.data?.cveDetailsRecord ||
                          currentDetailsType === 'expired-ssl-certificate' && sslCertDetails?.totalResults ||
                          currentDetailsType === 'vulnerable-ssl-certificate' && vulnSslCertDetails?.totalResults ||
                          currentDetailsType === 'vulnerable-obsolete-technology' && obsoleteTechDetails?.data?.totalTechs ||
                          currentDetailsType === 'stolen-cred' && stolenCredDetails?.data?.totalLeakedCredentials ||
                          currentDetailsType === 'malware-cred' && malwareCredDetails?.data?.totalMalwareCredentials ||
                          currentDetailsType === 'personal-information-breach' && piiDetails?.data?.totalPersonalInformationLeakage ||
                          currentDetailsType === 'leaked-session' && leakedSessionDetails?.data?.totalLeakedSessions ||
                          currentDetailsType === 'dangling-subdomain' && danglingSubdomainDetails?.data?.totalDomains ||
                          currentDetailsType === 'dark-web-mention' && darkWebMentionDetails?.totalCount ||
                          currentDetailsType === 'postman' && postmanDetails?.totalCount ||
                          currentDetailsType === 'telegram' && telegramDetails?.data?.totalRecords ||
                          currentDetailsType === 'spf' && spfDetails?.totalDocs ||
                          currentDetailsType === 'dmarc' && dmarcDetails?.totalDocs ||
                          currentDetailsType === 'leaked-code' && leakedCodeDetails?.data?.totalLeakedCodes
                        }
                        pageSize={itemsPerPage}
                        onPageChange={(page) => handleViewDetails(selectedOrgId, currentDetailsType, page, searchTerm)}
                      />
                    )}
                </div>
              </div>
              <div className="modal-footer bg-light">
                <div className="d-flex justify-content-between w-100">
                  <div>
                    <span className="text-muted">
                      Last Updated: {new Date().toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <button type="button" className="btn btn-round btn-default me-2" onClick={closeDetailsModal}>
                      Close
                    </button>
                    {/* <button type="button" className="btn btn-primary">
                      <FontAwesomeIcon icon={faDownload} className="mr-2" style={{ display: 'inline-block' }} />
                      Export Report
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardVendor;